import { Dialog, IconButton } from "@mui/material";
import { useEffect, useState } from "react";
import intl from "react-intl-universal";
import { MdClose } from "react-icons/md";
import { TbArrowBackUp, TbCheck, TbReceiptRefund, TbX } from "react-icons/tb";
import { useBaasCapabilities } from "../../../../../Hooks/useBaasCapabilities";
import { formatCurrency } from "../../../../../Utils/formatters";
import {
  getBaasTransactionMetadataEntries,
  getBaasTransactionMetadataLabelKey,
  isBaasTransactionPendingActionEligible,
  isBaasTransactionRefundEligible,
} from "../../../../../Services/baasTransactionsApi";
import { CrossborderCopyButton } from "../../../Crossborder/components/CrossborderCopyButton";
import { BaasTransactionStatusBadge } from "./BaasTransactionStatusBadge";
import { BaasTransactionActionModal } from "./BaasTransactionActionModal";

const DETAIL_TABS = [
  { id: "details", labelKey: "baas.transactions.detail.tabs.details" },
  { id: "metadata", labelKey: "baas.transactions.detail.tabs.metadata" },
];

const PANEL_CLASS =
  "!rounded-2xl !shadow-card app-card !m-4 !flex !flex-col !max-h-[90vh] !w-[calc(100%-2rem)] !max-w-3xl";

const DetailRow = ({ label, value, mono = false, copyValue }) => {
  if (value == null || value === "") return null;

  return (
    <div className="flex flex-col gap-0.5 border-b border-gray-100 py-2.5 last:border-b-0 dark:border-brand-dark-border sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
      <div className="flex min-w-0 items-center gap-1 sm:justify-end">
        <span
          className={`text-sm font-medium text-brand-dark dark:text-gray-100 sm:text-right ${mono ? "font-mono text-xs break-all" : ""}`}
        >
          {value}
        </span>
        {copyValue ? (
          <CrossborderCopyButton
            value={copyValue}
            ariaLabel={intl.get("baas.transactions.metadata.copyValue", { field: label })}
          />
        ) : null}
      </div>
    </div>
  );
};

const BaasTransactionDetailTabs = ({ activeTab, onTabChange }) => (
  <div
    className="extract-view-tabs extract-view-tabs--inline border-b border-gray-100 px-4 dark:border-brand-dark-border sm:px-5"
    role="tablist"
    aria-label={intl.get("baas.transactions.detail.title")}
  >
    {DETAIL_TABS.map((tab) => {
      const isActive = activeTab === tab.id;
      return (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={isActive}
          className={`extract-view-tab ${isActive ? "extract-view-tab--active" : ""}`}
          onClick={() => onTabChange(tab.id)}
        >
          {intl.get(tab.labelKey)}
        </button>
      );
    })}
  </div>
);

const PldRiskBadge = ({ level }) => {
  const styles = {
    low: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
    medium: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
    high: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400",
  };

  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${styles[level] ?? styles.low}`}>
      {intl.get(`baas.transactions.pld.riskLevel.${level}`)}
    </span>
  );
};

const formatDateTime = (value) => {
  if (!value) return "—";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "short", timeStyle: "short" }).format(
    new Date(value),
  );
};

const formatAmount = (amount, currency) => formatCurrency(amount, currency);

const getMetadataFieldLabel = (fieldKey) => {
  const labelKey = getBaasTransactionMetadataLabelKey(fieldKey);
  const label = intl.get(labelKey);
  return label === labelKey ? fieldKey : label;
};

const BTN_PRIMARY = "btn-primary inline-flex items-center gap-2";
const BTN_OUTLINE =
  "btn-outline inline-flex items-center gap-2 dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100";
const BTN_OUTLINE_DANGER =
  "btn-outline inline-flex items-center gap-2 text-red-600 dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-red-400";

export const BaasTransactionDetailDrawer = ({
  transaction,
  onClose,
  onRefundRequest,
  onRefundSuccess,
}) => {
  const [pendingAction, setPendingAction] = useState(null);
  const [activeTab, setActiveTab] = useState("details");
  const { can } = useBaasCapabilities();
  const canWriteTx = can("accounts.transfer");
  const isOpen = Boolean(transaction);

  useEffect(() => {
    setActiveTab("details");
  }, [transaction?.id]);

  if (!transaction) {
    return null;
  }

  const { pld } = transaction;
  const canRefund = canWriteTx && isBaasTransactionRefundEligible(transaction);
  const canPendingAction =
    canWriteTx && isBaasTransactionPendingActionEligible(transaction);
  const metadataEntries = getBaasTransactionMetadataEntries(transaction);

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
        PaperProps={{ className: PANEL_CLASS }}
      >
        <div className="flex items-start justify-between gap-2 border-b border-gray-100 px-4 py-3 dark:border-brand-dark-border sm:px-5">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              {intl.get("baas.transactions.detail.title")}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <h3 className="truncate font-mono text-sm text-brand-dark dark:text-gray-100">
                #{transaction.referenceId}
              </h3>
              <BaasTransactionStatusBadge
                status={transaction.status}
                refunded={transaction.refunded}
                returned={transaction.returned}
              />
              <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-700 dark:bg-brand-dark-elevated dark:text-gray-300">
                {transaction.rail || transaction.type}
              </span>
            </div>
          </div>
          <IconButton aria-label={intl.get("words.close")} onClick={onClose} size="small" className="!-mr-1">
            <MdClose />
          </IconButton>
        </div>

        <BaasTransactionDetailTabs activeTab={activeTab} onTabChange={setActiveTab} />

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5">
          {activeTab === "details" ? (
            <>
              <p className="dashboard-section-label mb-2">
                {intl.get("baas.transactions.detail.overview")}
              </p>
              <div className="app-card mb-5 !p-0 !shadow-none">
                <div className="px-4">
                  <DetailRow
                    label={intl.get("baas.transactions.columns.entity")}
                    value={transaction.entityName || transaction.clientName}
                  />
                  <DetailRow
                    label={intl.get("baas.transactions.columns.instance")}
                    value={transaction.instanceName}
                  />
                  <DetailRow
                    label={intl.get("baas.transactions.columns.transaction")}
                    value={transaction.description || transaction.counterpartyName}
                  />
                  {transaction.description &&
                  transaction.counterpartyName &&
                  transaction.description !== transaction.counterpartyName ? (
                    <DetailRow
                      label={intl.get("baas.transactions.detail.counterparty")}
                      value={transaction.counterpartyName}
                    />
                  ) : null}
                  <DetailRow
                    label={intl.get("baas.transactions.detail.counterpartyDocument")}
                    value={transaction.counterpartyDocument}
                    mono
                  />
                  <DetailRow
                    label={intl.get("baas.transactions.columns.amount")}
                    value={formatAmount(transaction.amount, transaction.currency)}
                  />
                  <DetailRow
                    label={intl.get("baas.transactions.detail.fee")}
                    value={formatAmount(transaction.fee ?? 0, transaction.currency)}
                  />
                  <DetailRow
                    label={intl.get("baas.transactions.detail.direction")}
                    value={intl.get(`baas.transactions.direction.${transaction.direction}`)}
                  />
                  <DetailRow
                    label={intl.get("baas.transactions.columns.date")}
                    value={formatDateTime(transaction.createdAt)}
                  />
                  {transaction.processedAt ? (
                    <DetailRow
                      label={intl.get("baas.transactions.detail.processedAt")}
                      value={formatDateTime(transaction.processedAt)}
                    />
                  ) : null}
                  <DetailRow
                    label={intl.get("baas.transactions.detail.id")}
                    value={transaction.id}
                    mono
                    copyValue={transaction.id}
                  />
                </div>
              </div>

              {false ? (
              <>
              <p className="dashboard-section-label mb-2">
                {intl.get("baas.transactions.pld.title")}
              </p>
              <div className="app-card mb-5 !p-4 !shadow-none">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-brand-dark dark:text-gray-100">
                    {intl.get("baas.transactions.pld.riskScore")}: {pld?.riskScore ?? "—"}
                  </span>
                  {pld?.riskLevel ? <PldRiskBadge level={pld.riskLevel} /> : null}
                </div>

                <div className="space-y-0">
                  <DetailRow
                    label={intl.get("baas.transactions.pld.screening")}
                    value={
                      pld?.screeningResult
                        ? intl.get(`baas.transactions.pld.screeningResult.${pld.screeningResult}`)
                        : "—"
                    }
                  />
                  <DetailRow
                    label={intl.get("baas.transactions.pld.pep")}
                    value={
                      pld?.pepCheck
                        ? intl.get(`baas.transactions.pld.pepCheck.${pld.pepCheck}`)
                        : "—"
                    }
                  />
                  <DetailRow
                    label={intl.get("baas.transactions.pld.sanctions")}
                    value={
                      pld?.sanctionsCheck
                        ? intl.get(`baas.transactions.pld.sanctionsCheck.${pld.sanctionsCheck}`)
                        : "—"
                    }
                  />
                </div>

                {pld?.flags?.length ? (
                  <div className="mt-3">
                    <p className="mb-1.5 text-xs text-gray-500 dark:text-gray-400">
                      {intl.get("baas.transactions.pld.flags")}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {pld.flags.map((flag) => (
                        <span
                          key={flag}
                          className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-950/40 dark:text-amber-400"
                        >
                          {intl.get(`baas.transactions.pld.flag.${flag}`)}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}

                {pld?.analystNotes ? (
                  <div className="mt-3 rounded-lg border border-gray-100 bg-gray-50/80 p-3 dark:border-brand-dark-border dark:bg-brand-dark-elevated/50">
                    <p className="mb-1 text-xs font-semibold text-gray-500 dark:text-gray-400">
                      {intl.get("baas.transactions.pld.analystNotes")}
                    </p>
                    <p className="text-sm text-brand-dark dark:text-gray-200">{pld.analystNotes}</p>
                  </div>
                ) : null}

                {pld?.reviewedAt ? (
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    {intl.get("baas.transactions.pld.reviewedAt")}: {formatDateTime(pld.reviewedAt)}
                  </p>
                ) : null}
              </div>
              </>
              ) : null}
            </>
          ) : (
            <>
              <p className="dashboard-section-label mb-2">
                {intl.get("baas.transactions.metadata.title")}
              </p>
              {metadataEntries.length ? (
                <div className="app-card !p-0 !shadow-none">
                  <div className="px-4">
                    {metadataEntries.map(({ key, value }) => (
                      <DetailRow
                        key={key}
                        label={getMetadataFieldLabel(key)}
                        value={value}
                        mono
                        copyValue={String(value)}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="app-card !p-4 !shadow-none">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {intl.get("baas.transactions.metadata.empty")}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {canPendingAction || canRefund ? (
          <div className="shrink-0 border-t border-gray-100 px-4 py-3 dark:border-brand-dark-border sm:px-5">
            <div className="flex flex-wrap items-center justify-end gap-2">
              {canPendingAction ? (
                <>
                  <button
                    type="button"
                    className={BTN_OUTLINE_DANGER}
                    onClick={() => setPendingAction("reject")}
                  >
                    <TbX className="h-4 w-4" aria-hidden />
                    {intl.get("baas.transactions.actions.reject.action")}
                  </button>
                  <button
                    type="button"
                    className={BTN_OUTLINE_DANGER}
                    onClick={() => setPendingAction("return")}
                  >
                    <TbArrowBackUp className="h-4 w-4" aria-hidden />
                    {intl.get("baas.transactions.actions.return.action")}
                  </button>
                  <button
                    type="button"
                    className={BTN_PRIMARY}
                    onClick={() => setPendingAction("release")}
                  >
                    <TbCheck className="h-4 w-4" aria-hidden />
                    {intl.get("baas.transactions.actions.release.action")}
                  </button>
                </>
              ) : null}
              {canRefund ? (
                <button
                  type="button"
                  className={BTN_OUTLINE}
                  onClick={() => {
                    onRefundRequest?.(transaction);
                    onClose?.();
                  }}
                >
                  <TbReceiptRefund className="h-4 w-4" aria-hidden />
                  {intl.get("baas.transactions.refund.action")}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </Dialog>

      <BaasTransactionActionModal
        open={Boolean(pendingAction)}
        action={pendingAction}
        transaction={transaction}
        onClose={() => setPendingAction(null)}
        onSuccess={() => {
          setPendingAction(null);
          onRefundSuccess?.(transaction.id);
        }}
      />
    </>
  );
};
